<script lang="ts" setup>
import type { IconName } from "../Icon/icons";
import type { LanguageSwitchGroup, LanguageSwitchOption } from "../LanguageSwitch/VLanguageSwitch.types";
import { computed, getCurrentInstance, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import ACTooltip from "../ACTooltip/ACTooltip.vue";
import VFieldLabel from "../Form/VFieldLabel.vue";
import VIcon from "../Icon/VIcon.vue";
import VLanguageSwitch from "../LanguageSwitch/VLanguageSwitch.vue";
import VTranslationNotices from "../LanguageSwitch/VTranslationNotices.vue";
import VSkeleton from "../Skeleton/VSkeleton.vue";
import { useFieldId } from "../useFieldId";
import {
  errorMessageCva,
  iconLeftCva,
  iconRightCva,
  inputContentCva,
  inputCva,
  statusMessageCva,
  subWrapperCva,
  wrapperCva,
} from "./VInput.cva";

const {
  type = "text",
  iconLeft = undefined,
  iconRight = undefined,
  name = "",
  autocomplete = "off",
  autocapitalize = "on",
  editable = true,
  disabled = false,
  loading = false,
  variant = "is-gray",
  textAlign = "is-text-align-left",
  errorMessage = "",
  tone = "default",
  statusMessage = "",
  statusIcon = undefined,
  help = "",
  hasTopLabel = false,
  labelLeft = "",
  labelCenter = false,
  hasMargin = false,
  layout = "is-column",
  required = false,
  initialValue = undefined,
  showErrorBelow = false,
  revealToggle = true,
  hasPerms = true,
  disabledReason = undefined,
  invalid = false,
  size = "medium",
  ariaLabel = "",
  ariaRequired = false,
  ariaHidden = false,
  ariaDescribedby = undefined,
  ariaLabelledby = undefined,
  role = undefined,
  ariaExpanded = undefined,
  ariaControls = undefined,
  ariaActivedescendant = undefined,
  ariaAutocomplete = undefined,
  clearable = false,
  id: rawId = undefined,
  placeholder = "",
  maxDecimals = null,
  min = undefined,
  maxLength = undefined,
  minLength = undefined,
  pattern = undefined,
  inputMode = undefined,
  readonly = false,
  hasIconRightAction = false,
  languages = [],
  languageGroups = [],
  languageSelectLabel = undefined,
  showTranslateNotice = false,
  translateNoticeText = "",
  translateAutomaticallyLabel = "",
  showOutdatedNotice = false,
  outdatedNoticeText = "",
  refreshLabel = "",
  refreshing = false,
} = defineProps<Props>();

const emit = defineEmits<{
  (e: "blur", event: Event): void
  (e: "onRightIconClick"): void
  (e: "keydown", event: KeyboardEvent): void
  (e: "clear"): void
  (e: "refresh"): void
}>();

const model = defineModel<InputModelValue>({ required: true });
const selectedLanguage = defineModel<string>("selectedLanguage", { default: "" });
const translateAutomatically = defineModel<boolean>("translateAutomatically", { default: false });

interface Props {
  type?: string
  iconRight?: IconName
  iconLeft?: IconName
  initialValue?: InputModelValue
  name?: string
  autocomplete?: string
  autocapitalize?: "on" | "off" | "words" | "sentences" | "characters"
  disabled?: boolean
  loading?: boolean
  variant?: string
  /** Aligns the typed text inside the field. */
  textAlign?: InputTextAlign
  hasPerms?: boolean
  /**
   * Why the field is disabled although the user may change it. Disables the field and shows this reason in a tooltip
   * instead of the permission message, which only explains a field the user lacks permission for.
   */
  disabledReason?: string
  errorMessage?: string
  /**
   * `success` marks a value the app checked and accepted (a reservation number that was found): a primary border and a
   * check icon on the right. An error message or `invalid` wins over it.
   */
  tone?: InputTone
  /**
   * A short line under the field about its value, such as what a lookup found. Announced politely (`role="status"`)
   * and linked to the input with `aria-describedby`. Hidden while an error is shown under the field.
   */
  statusMessage?: string
  /** Icon before the status message. Defaults to a check for the `success` tone, none otherwise. */
  statusIcon?: IconName
  /** Help text behind a question mark after the label; never a line under the field. */
  help?: string
  hasTopLabel?: boolean
  placeholder?: string
  labelCenter?: boolean
  hasMargin?: boolean
  hasIconRightAction?: boolean
  /** `is-column` puts the label above the field; `is-row` gives the label a third of the row beside the field. */
  layout?: InputLayout
  labelLeft?: string
  required?: boolean
  showErrorBelow?: boolean
  editable?: boolean
  invalid?: boolean
  size?: "medium" | "large"
  ariaLabel?: string
  ariaRequired?: boolean
  ariaHidden?: boolean
  ariaDescribedby?: string
  /** Id of an element that names the input, for a label the caller renders elsewhere instead of `labelLeft`. */
  ariaLabelledby?: string
  /** Role of the input, e.g. `combobox` for a field that suggests values in a list it controls. */
  role?: string
  /** Whether the list the input controls is shown; a string so the attribute is absent when not given. */
  ariaExpanded?: "true" | "false"
  /** Id of the list the input controls. */
  ariaControls?: string
  /** Id of the active option of that list while focus stays in the input. */
  ariaActivedescendant?: string
  ariaAutocomplete?: "none" | "inline" | "list" | "both"
  id?: string
  clearable?: boolean
  maxDecimals?: number | null
  min?: undefined | number
  /** Native `maxlength`: the browser stops typing past this many characters. */
  maxLength?: number
  /** Native `minlength`: the browser reports the field invalid below this many characters. */
  minLength?: number
  /** Native `pattern`: a regular expression the whole value must match for the browser to accept it. */
  pattern?: string
  /** Native `inputmode`: the virtual keyboard a phone shows, e.g. `decimal` for a price. */
  inputMode?: InputMode
  readonly?: boolean
  /**
   * Whether a password input shows its own eye inside the field. Off for a screen that toggles the plain value with a
   * button of its own beside the field (the type then decides what is shown).
   */
  revealToggle?: boolean
  /** Versions the reader can switch between; shows a compact language switch inside the input. */
  languages?: LanguageSwitchOption[]
  /** Headings for languages with a `group`, see `VLanguageSwitch`. */
  languageGroups?: LanguageSwitchGroup[]
  /** Accessible name of the language switch. Defaults to the lib "Choose a language" text. */
  languageSelectLabel?: string
  /** Pass `translateNoticeText` and `translateAutomaticallyLabel` with it. */
  showTranslateNotice?: boolean
  translateNoticeText?: string
  translateAutomaticallyLabel?: string
  /** Pass `outdatedNoticeText` and `refreshLabel` with it. */
  showOutdatedNotice?: boolean
  outdatedNoticeText?: string
  refreshLabel?: string
  refreshing?: boolean
}

const isDisabled = computed(() => {
  return disabled || !hasPerms || Boolean(disabledReason);
});

export type InputModelValue = string | number | null | unknown;
export type InputTextAlign = "is-text-align-left" | "is-text-align-center" | "is-text-align-right";
export type InputLayout = "is-column" | "is-row";
export type InputTone = "default" | "success";
export type InputMode = "none" | "text" | "decimal" | "numeric" | "tel" | "search" | "email" | "url";

const id = useFieldId(() => rawId);
const revealPass = ref(false);
const valueRef = ref(initialValue || model.value);

function onInput(event: Event) {
  const input = event.target as HTMLInputElement;
  let value: string = input.value;

  // Handle minimum number validation first
  if (min !== undefined && value !== "") {
    const numValue = Number(value);
    if (!Number.isNaN(numValue) && numValue < min) {
      value = min.toString();
      model.value = value;
      valueRef.value = value;
      return;
    }
  }

  if (!value) {
    // Text inputs have no `min`; writing `min` (undefined) there poisons string v-models.
    const cleared = type === "number" ? min : "";
    model.value = cleared;
    valueRef.value = cleared;
    return;
  }
  if (maxDecimals === null) {
    return;
  }

  if (maxDecimals === 0) {
    // For maxDecimals=0, remove any decimal portion
    const dotIndex = value.indexOf(".");
    const commaIndex = value.indexOf(",");
    if (dotIndex !== -1 || commaIndex !== -1) {
      const decimalIndex = Math.max(dotIndex, commaIndex);
      value = value.slice(0, decimalIndex);
      model.value = value;
      valueRef.value = value;
    }
    return;
  }

  const commaIndex = value.indexOf(".");
  if (commaIndex === -1) {
    return;
  }

  value = value.slice(0, commaIndex + maxDecimals + 1);
  model.value = value;
  valueRef.value = value;
}

watch(
  () => model.value,
  (newValue, oldValue) => {
    if (newValue === oldValue) {
      return;
    }
    valueRef.value = newValue;
  },
);

watch(valueRef, (value, oldValue) => {
  if (value === oldValue) {
    return;
  }
  model.value = value;
});

const computedType = computed(() => {
  if (type !== "password") {
    return type;
  }

  if (revealPass.value) {
    return "text";
  }

  return type;
});

const computedIconRight = computed<IconName | undefined>(() => {
  if (type === "password" && revealToggle) {
    if (revealPass.value) {
      return "eye-off";
    }

    return "eye";
  }
  if (type === "date") {
    return "calendar";
  }

  if (type === "time") {
    return "clock";
  }

  return iconRight;
});

const instance = getCurrentInstance();

// Declared emits are stripped from $attrs, so the parent's listener is read from the vnode props.
// It is read per render (no computed) because the vnode is not reactive.
function hasRightIconListener(): boolean {
  return Boolean(instance?.vnode.props?.onOnRightIconClick);
}

function onRightIconClick() {
  if (type === "password" && revealToggle) {
    revealPass.value = !revealPass.value;
  } else {
    emit("onRightIconClick");
  }
}

const showErrorUnder = computed(() => {
  return showErrorBelow || (errorMessage && model.value);
});

const errorId = computed(() => `${id.value}-error`);
const statusId = computed(() => `${id.value}-status`);

const hasError = computed(() => Boolean(errorMessage) || invalid);
const isSuccess = computed(() => tone === "success" && !hasError.value);
const showStatusMessage = computed(() => Boolean(statusMessage) && !(showErrorUnder.value && errorMessage));
const computedStatusIcon = computed<IconName | undefined>(() => {
  if (statusIcon) {
    return statusIcon;
  }
  return isSuccess.value ? "circle-check" : undefined;
});

const computedAriaInvalid = computed(() => {
  if (errorMessage || invalid) {
    return "true";
  }
  return undefined;
});

const computedAriaDescribedby = computed(() => {
  const parts: string[] = [];
  if (ariaDescribedby) {
    parts.push(ariaDescribedby);
  }
  if (showErrorUnder.value && errorMessage) {
    parts.push(errorId.value);
  }
  if (showStatusMessage.value) {
    parts.push(statusId.value);
  }
  if (parts.length === 0) {
    return undefined;
  }
  return parts.join(" ");
});

const placeholderText = computed(() => {
  if (errorMessage && !showErrorUnder.value) {
    return errorMessage;
  }
  return placeholder;
});

function clearInput() {
  valueRef.value = "";
  emit("clear");
}

const showClearIcon = computed(() => {
  return clearable && valueRef.value;
});

const hasLanguages = computed(() => languages.length > 0);
const { t } = useI18n();

// The success check takes the right icon slot only when nothing else (clear, reveal, a caller's icon) uses it.
const showSuccessIcon = computed(() =>
  isSuccess.value && !hasLanguages.value && !showClearIcon.value && !computedIconRight.value,
);

const wrapperClasses = computed(() => wrapperCva());

const subWrapperClasses = computed(() =>
  subWrapperCva({
    layout,
    loading,
    editable,
    disabled: isDisabled.value,
    labelCenter,
    hasMargin,
  }),
);

const inputClasses = computed(() =>
  inputCva({
    variant: variant as "is-gray" | "is-white" | "is-transparent",
    textAlign,
    size: size as "medium" | "large",
    hasTopLabel,
    hasIconLeft: !!iconLeft,
    hasIconRight: !hasLanguages.value && !!(iconRight || (type === "password" && revealToggle) || showClearIcon.value || showSuccessIcon.value),
    hasValidationError: hasError.value,
    tone: isSuccess.value ? "success" : "default",
  }),
);

const inputContentClasses = computed(() =>
  inputContentCva({
    size: size as "medium" | "large",
  }),
);

const iconLeftClasses = computed(() => iconLeftCva());

const iconRightClasses = computed(() =>
  iconRightCva({
    clearable: !!showClearIcon.value,
    hasAction:
      (type === "password" && revealToggle)
      || type === "date"
      || type === "time"
      || hasIconRightAction,
  }),
);

const errorMessageClasses = computed(() => errorMessageCva());
const statusMessageClasses = computed(() => statusMessageCva({ tone: isSuccess.value ? "success" : "default" }));

const iconButtonFocusClasses = "rounded-full focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500";
</script>

<template>
  <div :class="wrapperClasses">
    <div
      :class="[
        subWrapperClasses,
        {
          'is-disabled': isDisabled,
          'is-not-editable': !editable,
        },
      ]"
    >
      <VFieldLabel
        v-if="labelLeft"
        :label="labelLeft"
        :for="id"
        :required="required"
        :layout="layout"
        display="inline"
        :help="help"
      />
      <!-- <VSkeleton
        v-if="loading"
        :height="32"
        :width="100"
        width-unit="%"
        :dynamic-width="false"
        style="border-radius: 5px"
        :pill="false"
      /> -->
      <div class="flex items-center gap-2">
        <ACTooltip
          type="edit_field"
          :allow-change="hasPerms"
          :disabled-reason="disabledReason"
          class="flex-1"
        >
          <div
            class="input"
            :class="[inputClasses]"
            :style="{ width: layout === 'is-row' ? 'calc(100% - 33.33%)' : '100%' }"
          >
            <div v-if="iconLeft" :class="iconLeftClasses">
              <VIcon :name="iconLeft" aria-hidden="true" />
            </div>
            <div
              v-if="loading"
              class="input-content flex items-center"
            >
              <VSkeleton :width="140" width-unit="px" :height="size === 'medium' ? 10 : 12" height-unit="px" pill />
            </div>
            <input
              v-else
              :id="id"
              v-model="valueRef"
              :name="name"
              :type="computedType"
              :class="[inputContentClasses, { 'min-w-0': hasLanguages }]"
              :min="min"
              :maxlength="maxLength"
              :minlength="minLength"
              :pattern="pattern"
              :inputmode="inputMode"
              :placeholder="placeholderText"
              :disabled="isDisabled || !editable"
              :readonly="readonly"
              :autocomplete="autocomplete"
              :autocapitalize="autocapitalize"
              :aria-label="ariaLabel"
              :aria-labelledby="ariaLabelledby"
              :role="role"
              :aria-expanded="ariaExpanded"
              :aria-controls="ariaControls"
              :aria-activedescendant="ariaActivedescendant"
              :aria-autocomplete="ariaAutocomplete"
              :aria-required="ariaRequired"
              :aria-hidden="ariaHidden"
              :aria-invalid="computedAriaInvalid"
              :aria-describedby="computedAriaDescribedby"
              @input="onInput"
              @blur="$emit('blur', $event)"
              @keydown="$emit('keydown', $event)"
            >
            <div
              v-if="hasLanguages"
              class="flex shrink-0 items-center gap-1.5 pr-1"
              data-test="input-language"
            >
              <button
                v-if="showClearIcon"
                type="button"
                class="inline-flex items-center px-1 text-sm text-gray-800 dark:text-gray-200"
                :aria-label="t('base.clear')"
                @click="clearInput"
              >
                <VIcon name="x" class="font-bold" aria-hidden="true" />
              </button>
              <VLanguageSwitch
                v-model="selectedLanguage"
                :languages="languages"
                :groups="languageGroups"
                :label="languageSelectLabel"
                :disabled="isDisabled"
                variant="compact"
                placement="bottom-end"
              />
            </div>
            <button
              v-else-if="showClearIcon"
              type="button"
              :class="[iconRightClasses, iconButtonFocusClasses]"
              :aria-label="t('base.clear')"
              @click="clearInput"
            >
              <VIcon name="x" class="font-bold" aria-hidden="true" />
            </button>
            <button
              v-else-if="computedIconRight && type === 'password'"
              type="button"
              :class="[iconRightClasses, iconButtonFocusClasses]"
              :aria-label="revealPass ? t('base.password_hide') : t('base.password_show')"
              :aria-pressed="revealPass"
              @click="onRightIconClick"
            >
              <VIcon :name="computedIconRight" aria-hidden="true" />
            </button>
            <button
              v-else-if="computedIconRight && hasRightIconListener()"
              type="button"
              :class="[iconRightClasses, iconButtonFocusClasses]"
              :aria-label="t('base.clear')"
              @click="onRightIconClick"
            >
              <VIcon :name="computedIconRight" aria-hidden="true" />
            </button>
            <div
              v-else-if="computedIconRight"
              :class="iconRightClasses"
              aria-hidden="true"
            >
              <VIcon :name="computedIconRight" aria-hidden="true" />
            </div>
            <div
              v-else-if="showSuccessIcon"
              class="text-primary-600! dark:text-primary-400!"
              :class="iconRightClasses"
              aria-hidden="true"
              data-test="input-success-icon"
            >
              <VIcon name="check" :stroke-width="2.5" aria-hidden="true" />
            </div>
          </div>
        </ACTooltip>
        <slot name="suffix" />
      </div>
      <div
        v-if="showErrorUnder && errorMessage"
        :id="errorId"
        :class="errorMessageClasses"
      >
        {{ errorMessage }}
      </div>
      <div
        v-if="showStatusMessage"
        :id="statusId"
        :class="statusMessageClasses"
        role="status"
        data-test="input-status-message"
      >
        <VIcon v-if="computedStatusIcon" :name="computedStatusIcon" class="shrink-0" aria-hidden="true" />
        <span class="min-w-0">{{ statusMessage }}</span>
      </div>
      <div
        v-if="hasLanguages && (showTranslateNotice || showOutdatedNotice)"
        class="mt-1.5 flex flex-col gap-1.5 px-2"
        data-test="input-translation-notices"
      >
        <VTranslationNotices
          v-model:translate-automatically="translateAutomatically"
          :show-translate-notice="showTranslateNotice"
          :translate-notice-text="translateNoticeText"
          :translate-automatically-label="translateAutomaticallyLabel"
          :show-outdated-notice="showOutdatedNotice"
          :outdated-notice-text="outdatedNoticeText"
          :refresh-label="refreshLabel"
          :refreshing="refreshing"
          :disabled="isDisabled"
          @refresh="emit('refresh')"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
:deep(.is-not-editable input:disabled) {
  cursor: inherit;
}

:deep(.is-disabled) {
  .input {
    cursor: not-allowed;
    opacity: 0.5;
  }

  input:disabled {
    cursor: not-allowed;
  }
}
</style>
